import React, { useState } from "react";

import { MAX_CONCURRENT_JOBS } from "./ingestJobs";
import { Modal, PrimaryButton, SecondaryButton } from "./shared";

/**
 * Guard rail for the batch we send to the AI. Generous enough for a big JSON
 * paste of many products, but blocks an accidental "paste the whole file"
 * payload. The backend enforces its own (larger) hard cap on the route body.
 */
const MAX_INPUT_BYTES = 256 * 1024; // 256 KB of product text

const byteLen = (s: string): number =>
  typeof TextEncoder !== "undefined"
    ? new TextEncoder().encode(s).length
    : // Fallback: rough UTF-8 estimate.
      unescape(encodeURIComponent(s)).length;

const fmtKb = (bytes: number): string => `${(bytes / 1024).toFixed(1)} KB`;

interface StructuredRow {
  name: string;
  brand: string;
  upc: string;
  url: string;
}

const emptyRow = (): StructuredRow => ({
  name: "",
  brand: "",
  upc: "",
  url: "",
});

interface IngestTabProps {
  ingestChainId: string;
  /** Start a background research run. Rejects (IngestJobLimitError) if full. */
  onSubmit: (foodInput: string, productCount: number) => Promise<void>;
  /** How many runs are currently in flight (for the concurrency guard). */
  activeCount: number;
}

const IngestTab: React.FC<IngestTabProps> = ({
  ingestChainId,
  onSubmit,
  activeCount,
}) => {
  const [freeText, setFreeText] = useState("");
  const [rows, setRows] = useState<StructuredRow[]>([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [runError, setRunError] = useState<string>("");
  const [startedNote, setStartedNote] = useState<string>("");

  const atCapacity = activeCount >= MAX_CONCURRENT_JOBS;

  const updateRow = (i: number, patch: Partial<StructuredRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const nonEmptyRows = rows.filter(
    (r) => r.name.trim() || r.brand.trim() || r.upc.trim() || r.url.trim()
  );

  const freeTextCount = freeText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean).length;
  const productCount = freeTextCount + nonEmptyRows.length;

  const buildFoodInput = (): string => {
    const parts: string[] = [];
    if (freeText.trim()) parts.push(freeText.trim());
    if (nonEmptyRows.length > 0) {
      parts.push(
        JSON.stringify(
          nonEmptyRows.map((r) => ({
            name: r.name.trim() || undefined,
            brand: r.brand.trim() || undefined,
            upc: r.upc.trim() || undefined,
            url: r.url.trim() || undefined,
          }))
        )
      );
    }
    return parts.join("\n");
  };

  const inputBytes = byteLen(buildFoodInput());
  const overLimit = inputBytes > MAX_INPUT_BYTES;

  const canRun =
    !submitting &&
    !overLimit &&
    !atCapacity &&
    (freeText.trim().length > 0 || nonEmptyRows.length > 0);

  const run = async () => {
    setError("");
    setRunError("");
    setStartedNote("");
    const foodInput = buildFoodInput();
    if (!foodInput) {
      setError(
        "Add at least one product (free-form text or a structured row)."
      );
      return;
    }
    if (byteLen(foodInput) > MAX_INPUT_BYTES) {
      setError(
        `That's a lot of text (${fmtKb(
          byteLen(foodInput)
        )}). Keep it under ${fmtKb(
          MAX_INPUT_BYTES
        )} — split very large batches into a couple of runs.`
      );
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(foodInput, productCount);
      // Started in the background — clear the form so another batch can be
      // queued, and point the user at the runs tray.
      setFreeText("");
      setRows([emptyRow()]);
      setStartedNote(
        "Research started — it runs in the background. Track progress in the Ingest runs panel (bottom-right); we'll surface the results there when it's done."
      );
    } catch (err) {
      const e = err as Error;
      if (e?.name === "IngestJobLimitError") {
        setError(e.message);
      } else {
        setRunError(e?.message || "Ingest pipeline failed to start");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Step 1 — Add your products
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Just list your products — one per line. A{" "}
          <span className="font-medium">name</span> is enough; a{" "}
          <span className="font-medium">UPC/barcode</span> or{" "}
          <span className="font-medium">link</span> helps too. We research the
          rest (nutrition, ingredients, country, tags) and check each one before
          you write anything.
        </p>
      </div>

      {/* PRIMARY input: paste a list, one product per line. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className="text-sm font-semibold text-gray-800">
          Paste your products
          <span className="ml-2 text-xs font-normal text-gray-400">
            one per line — names, barcodes, or links (mix freely)
          </span>
        </label>
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          rows={8}
          disabled={submitting}
          placeholder={
            "MariGold Pure Joy Protein Bar\n" +
            "Keto Bars Chocolate Peanut Butter Keto Bar\n" +
            "DNX Grass-Fed Beef & Uncured Bacon Jalapeño Bar\n" +
            "850002251234\n" +
            "https://www.instacart.com/products/...\n" +
            '{"name":"Base Culture Keto Bread","brand":"Base Culture","upc":"..."}'
          }
          className="mt-2 w-full rounded-md border border-gray-300 p-3 font-mono text-sm focus:border-indigo-500 focus:outline-none"
        />
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Plain names work great. You can also drop in a JSON array if you
            have structured data.
          </p>
          <span
            className={`text-xs tabular-nums ${
              overLimit ? "font-semibold text-red-600" : "text-gray-400"
            }`}
          >
            {fmtKb(inputBytes)} / {fmtKb(MAX_INPUT_BYTES)}
          </span>
        </div>
        {overLimit && (
          <p className="mt-1 text-xs font-medium text-red-600">
            This batch is too large to send. Trim it or split it into a couple
            of smaller runs.
          </p>
        )}
      </div>

      {/* Clear "and / or" — the rows below are optional. */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          and / or
        </span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      {/* OPTIONAL structured rows for when you want to be precise per field. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-gray-800">
            Add products as rows{" "}
            <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <span className="text-xs text-gray-400">
            Only one of name / UPC / link is required per row
          </span>
        </div>

        {/* Column headers so it's clear what each field is and what's optional */}
        <div className="mt-3 hidden grid-cols-12 gap-2 px-1 text-xs font-medium text-gray-500 sm:grid">
          <div className="col-span-4">Product name</div>
          <div className="col-span-3">Brand (optional)</div>
          <div className="col-span-2">UPC (optional)</div>
          <div className="col-span-2">Link (optional)</div>
          <div className="col-span-1" />
        </div>

        <div className="mt-1 space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input
                className="col-span-12 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-4"
                placeholder="Product name"
                value={r.name}
                disabled={submitting}
                onChange={(e) => updateRow(i, { name: e.target.value })}
              />
              <input
                className="col-span-12 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-3"
                placeholder="Brand"
                value={r.brand}
                disabled={submitting}
                onChange={(e) => updateRow(i, { brand: e.target.value })}
              />
              <input
                className="col-span-6 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-2"
                placeholder="UPC"
                value={r.upc}
                disabled={submitting}
                onChange={(e) => updateRow(i, { upc: e.target.value })}
              />
              <input
                className="col-span-5 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-2"
                placeholder="Link"
                value={r.url}
                disabled={submitting}
                onChange={(e) => updateRow(i, { url: e.target.value })}
              />
              <button
                type="button"
                className="col-span-1 rounded-md text-gray-400 hover:text-red-600 disabled:opacity-40"
                disabled={submitting || rows.length === 1}
                onClick={() =>
                  setRows((rs) => rs.filter((_, idx) => idx !== i))
                }
                aria-label="Remove row"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <SecondaryButton
            type="button"
            onClick={() => setRows((rs) => [...rs, emptyRow()])}
            disabled={submitting}
          >
            + Add row
          </SecondaryButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <PrimaryButton type="button" onClick={run} disabled={!canRun}>
          {submitting ? "Starting…" : "Research products →"}
        </PrimaryButton>
        {!ingestChainId && (
          <span className="text-sm text-amber-700">
            No ingest chain id configured — set it in settings (top-right gear).
          </span>
        )}
        {atCapacity && ingestChainId && (
          <span className="text-sm text-amber-700">
            You have {MAX_CONCURRENT_JOBS} runs going — wait for one to finish
            (or clear it) in the Ingest runs panel before starting another.
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}
      {startedNote && !error && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {startedNote}
        </div>
      )}

      {/* Prominent failure dialog (replaces the old little red note). */}
      <Modal
        open={!!runError}
        tone="error"
        title="We couldn't finish researching"
        onClose={() => setRunError("")}
        footer={
          <>
            <SecondaryButton type="button" onClick={() => setRunError("")}>
              Close
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => {
                setRunError("");
                void run();
              }}
            >
              Try again
            </PrimaryButton>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          Something went wrong while researching your products. Nothing was
          saved to the catalog.
        </p>
        <div className="mt-3 rounded-md border border-red-100 bg-red-50 p-3 font-mono text-xs text-red-700">
          {runError}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          This is often a temporary hiccup with the research step. Trying again
          usually works. If it keeps failing, reduce the number of products or
          reach out to the team.
        </p>
      </Modal>
    </div>
  );
};

export default IngestTab;
